import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { createSiteSchema, type CreateSiteInput, type SiteFormOptions } from "@crewledger/shared/schemas";
import { PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createSite, fetchSite, fetchSiteOptions, updateSite } from "./api";
import { useSitePermissions } from "./use-site-permissions";

interface SiteFormPageProps {
  mode: "create" | "edit";
}

export function SiteFormPage({ mode }: SiteFormPageProps) {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { canWrite, canConfigureGeofence } = useSitePermissions();
  const [options, setOptions] = useState<SiteFormOptions | null>(null);
  const [loading, setLoading] = useState(mode === "edit");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<CreateSiteInput>({
    resolver: zodResolver(createSiteSchema),
    defaultValues: {
      name: "",
      code: "",
      address: "",
      status: "ACTIVE",
      defaultPayRuleProfileId: null,
      managerIds: [],
      notes: "",
      latitude: null,
      longitude: null,
      geofenceRadiusMeters: null,
      geofenceEnabled: false,
      geofenceStrictMode: false,
    },
  });

  useEffect(() => {
    if (!canWrite) return;
    fetchSiteOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions({ payRuleProfiles: [], managers: [] }); });
  }, [canWrite]);

  useEffect(() => {
    if (mode !== "edit" || !siteId) return;
    setLoading(true);
    fetchSite(siteId)
      .then((res) => {
        const site = res.data;
        form.reset({
          name: site.name,
          code: site.code,
          address: site.address ?? "",
          status: site.status,
          defaultPayRuleProfileId: site.defaultPayRuleProfileId,
          managerIds: site.managers.map((m) => m.id),
          notes: site.notes ?? "",
          latitude: site.latitude,
          longitude: site.longitude,
          geofenceRadiusMeters: site.geofenceRadiusMeters,
          geofenceEnabled: site.geofenceEnabled,
          geofenceStrictMode: site.geofenceStrictMode,
        });
      })
      .catch((err: unknown) =>
        { setSubmitError(err instanceof Error ? err.message : "Failed to load site"); },
      )
      .finally(() => { setLoading(false); });
  }, [mode, siteId, form]);

  if (!canWrite) {
    return (
      <div className="space-y-4">
        <PageHeader title="Access denied" description="You do not have permission to edit sites." />
        <Button variant="outline" onClick={() => navigate("/sites")}>
          Back to sites
        </Button>
      </div>
    );
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const payload = {
      ...values,
      address: values.address?.trim() ? values.address.trim() : null,
      notes: values.notes?.trim() ? values.notes.trim() : null,
      defaultPayRuleProfileId: values.defaultPayRuleProfileId || null,
      latitude: Number.isFinite(values.latitude) ? values.latitude : null,
      longitude: Number.isFinite(values.longitude) ? values.longitude : null,
      geofenceRadiusMeters: Number.isFinite(values.geofenceRadiusMeters)
        ? values.geofenceRadiusMeters
        : null,
    };
    try {
      if (mode === "create") {
        const res = await createSite(payload);
        navigate(`/sites/${res.data.id}`);
      } else if (siteId) {
        const res = await updateSite(siteId, payload);
        navigate(`/sites/${res.data.id}`);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save site");
    }
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title={mode === "create" ? "New site" : "Edit site"}
        description="Configure job site details, pay rules, and assigned managers."
      />

      {loading ? (
        <div className="h-96 animate-pulse rounded-xl border bg-card-white" />
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-6 rounded-xl border bg-card-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">Site name</Label>
              <Input id="name" {...form.register("name")} placeholder="Ridgepoint Tower — Phase 2" />
              {form.formState.errors.name?.message ? (
                <p className="text-xs text-red-danger">{String(form.formState.errors.name.message)}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Site code</Label>
              <Input id="code" {...form.register("code")} placeholder="SITE-042" className="font-mono uppercase" />
              {form.formState.errors.code?.message ? (
                <p className="text-xs text-red-danger">{String(form.formState.errors.code.message)}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                {...form.register("status")}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                {mode === "edit" ? <option value="CLOSED">Archived</option> : null}
              </select>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="address">Address</Label>
              <Input id="address" {...form.register("address")} placeholder="1847 Industrial Blvd, Austin TX" />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="defaultPayRuleProfileId">Default pay rule profile</Label>
              <select
                id="defaultPayRuleProfileId"
                value={form.watch("defaultPayRuleProfileId") ?? ""}
                onChange={(e) =>
                  { form.setValue("defaultPayRuleProfileId", e.target.value ? e.target.value : null); }
                }
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              >
                <option value="">None</option>
                {options?.payRuleProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label>Assigned managers</Label>
              <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border border-input p-3">
                {options?.managers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No eligible managers found.</p>
                ) : (
                  options?.managers.map((manager) => {
                    const selected = form.watch("managerIds").includes(manager.id);
                    return (
                      <label key={manager.id} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = form.getValues("managerIds");
                            form.setValue(
                              "managerIds",
                              e.target.checked
                                ? [...current, manager.id]
                                : current.filter((id: string) => id !== manager.id),
                            );
                          }}
                          className="rounded border-input"
                        />
                        <span className="font-medium text-navy-deep">{manager.name}</span>
                        <span className="text-muted-foreground">{manager.email}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                {...form.register("notes")}
                rows={4}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                placeholder="Site-specific instructions, gate codes, safety notes…"
              />
            </div>

            {canConfigureGeofence ? (
              <div className="space-y-4 rounded-lg border border-dashed border-navy-blueprint/30 p-4 sm:col-span-2">
                <div>
                  <h3 className="text-sm font-semibold text-navy-deep">Geofencing (beta)</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Optional site boundary for location checks on submit and future field clock.
                    Does not block manager entry unless strict mode is on.
                  </p>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" {...form.register("geofenceEnabled")} className="rounded" />
                  Enable geofencing for this site
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" {...form.register("geofenceStrictMode")} className="rounded" />
                  Strict mode — block submit when outside geofence or GPS unavailable
                </label>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      {...form.register("latitude", { valueAsNumber: true })}
                      placeholder="40.7128"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      {...form.register("longitude", { valueAsNumber: true })}
                      placeholder="-74.0060"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="geofenceRadiusMeters">Radius (meters)</Label>
                    <Input
                      id="geofenceRadiusMeters"
                      type="number"
                      {...form.register("geofenceRadiusMeters", { valueAsNumber: true })}
                      placeholder="150"
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {submitError ? <p className="text-sm text-red-danger">{submitError}</p> : null}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Saving…" : mode === "create" ? "Create site" : "Save changes"}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(mode === "edit" && siteId ? `/sites/${siteId}` : "/sites")}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export function SiteCreatePage() {
  return <SiteFormPage mode="create" />;
}

export function SiteEditPage() {
  return <SiteFormPage mode="edit" />;
}
